<script setup lang="ts">
import {
  DialogRoot,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogTitle,
  VisuallyHidden,
  DialogDescription,
} from 'reka-ui'
import { useThemeStore } from '@/stores/theme.ts'
import { useInfoStore } from '@/stores/info.ts'
import { useUiStore } from '@/stores/ui.store.ts'
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { Keyboard } from '@capacitor/keyboard'
import { PluginListenerHandle } from '@capacitor/core'

const { currentTheme } = useThemeStore()
const { isWeb } = useInfoStore()
const uiStore = useUiStore()
const keyboardOffset = ref(0)

const {
  closeOnBackdropClick = true,
  maxWidth,
  fullscreen,
} = defineProps<{
  closeOnBackdropClick?: boolean
  maxWidth?: number
  fullscreen?: boolean
}>()

const visible = defineModel('visible', { default: false })
const emit = defineEmits<{
  (e: 'open'): void
  (e: 'dismiss'): void
}>()

const myZLevel = ref(0)
const shaking = ref(false)

const dismissDialog = () => {
  visible.value = false
  emit('dismiss')
}

const shakeDialog = () => {
  shaking.value = true
  setTimeout(() => {
    shaking.value = false
  }, 400)
}

watch(visible, (newVal, oldVal) => {
  if (newVal && !oldVal) {
    myZLevel.value = uiStore.openDialogCount
    uiStore.pushDialog({
      close: dismissDialog,
      shake: shakeDialog,
      dismissable: closeOnBackdropClick,
    })
    emit('open')
  } else if (!newVal && oldVal) {
    uiStore.removeDialog(dismissDialog)
  }
})

onUnmounted(() => {
  if (visible.value) {
    uiStore.removeDialog(dismissDialog)
  }
})

if (!isWeb) {
  const listeners: PluginListenerHandle[] = []

  onMounted(async () => {
    listeners.push(
      await Keyboard.addListener('keyboardWillShow', (info) => {
        keyboardOffset.value = info.keyboardHeight / 2
      }),
      await Keyboard.addListener('keyboardWillHide', () => {
        keyboardOffset.value = 0
      }),
    )
  })

  onUnmounted(async () => {
    for (const listener of listeners) {
      await listener.remove()
    }
    listeners.length = 0
  })
}
</script>

<template>
  <DialogRoot v-model:open="visible">
    <DialogPortal>
      <DialogOverlay class="fixed inset-0 bg-black/75" :style="{ zIndex: 1000 + myZLevel * 100 }" />
      <DialogContent
        class="fixed left-1/2 top-1/2 overflow-hidden w-full flex flex-col focus:outline-none transition-transform duration-200"
        :class="[fullscreen ? 'h-full bg-transparent' : 'p-7.5', shaking ? 'animate-shake' : '']"
        :style="{
          zIndex: 1001 + myZLevel * 100,
          maxWidth: fullscreen ? undefined : `${maxWidth ?? currentTheme.layout.dialogWidth}px`,
          transform: `translate(-50%, calc(-50% - ${keyboardOffset}px))`,
        }"
      >
        <VisuallyHidden><DialogTitle>General Dialog</DialogTitle></VisuallyHidden>
        <VisuallyHidden><DialogDescription>General Dialog</DialogDescription></VisuallyHidden>
        <div
          class="bg-main-background"
          :class="[fullscreen ? 'h-full' : 'rounded-[15px]']"
        >
          <slot></slot>
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>
